import { useCallback, useMemo, useState } from "react";
import { SquareLock02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { BoomCrmPaywall } from "shared/app/BoomCrmPaywall/BoomCrmPaywall";
import { PAYWALL_COPY } from "shared/app/BoomCrmPaywall/constants";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { useAuthContext, useVoiceEnabled } from "providers/Auth";
import { composerTabs } from "../components/constants";
import { ChatDisabledTooltip } from "../components/ChatDisabledTooltip";
import { hasOutstandingInvite, useEligibleApplications } from "../api/hooks";
import { useCrmContact, useCrmLeadDetails } from "../api/crm";
import type { EligibleApplication } from "../types";

const updatedAtMs = (app: EligibleApplication): number => {
  // "" timestamp → NaN; treat invalid as oldest to keep the sort defined.
  const ms = new Date(app.application_updated_at).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const mostRecentApplicationId = (
  apps: EligibleApplication[] | undefined
): number | undefined => {
  if (!apps?.length) return undefined;
  return [...apps].sort((a, b) => updatedAtMs(b) - updatedAtMs(a))[0]
    .application_id;
};

/**
 * Whether a host should draw its channel chip row at all. Outside the CRM
 * ecosystem (a BoomScreen-only partner on native chat) there is normally a
 * single channel, so a lone "Chat" chip only restates what the window is and,
 * in the pop-up, takes space the thread needs (CRM-1325). The row stays when
 * that one chip is disabled — its tooltip is what explains why the channel is
 * unavailable — and always for CRM partners, whose chips carry the
 * locked-channel paywalls and the disabled Chat chip's "Invite to apply".
 */
export const shouldShowChannelTabs = ({
  isBoomCRM,
  tabs
}: {
  isBoomCRM: boolean;
  tabs: readonly { disabled?: boolean }[];
}) => isBoomCRM || tabs.length > 1 || tabs.some((tab) => tab.disabled);

// Shared composer tab list + chat gating for the message composer and the
// manual-communication form, so the Chat tab behaves identically on both.
export const useComposerTabs = (
  contactId?: string,
  options?: {
    inviteModalZIndex?: number;
    /**
     * Lead the host was opened from (the pop-up on a lead page). Invite-to-apply
     * then pre-selects that lead's community/unit and links the application to
     * it — the same props the lead page's own Invite button passes.
     */
    inviteLeadId?: number;
    hideInviteToApply?: boolean;
  }
) => {
  const { type } = useAuthContext();
  const isVoiceEnabled = useVoiceEnabled();

  const chatTabAvailable = type.isNativeChatActive;
  // Email and Call are Pro (communication_email / communication_phone). A CRM
  // partner without one keeps the tab, locked, so the upsell sits exactly where
  // they would reach for the channel instead of the channel silently not
  // existing (CRM-759; Figma BoomCRM-Paywall 31:23290 and 393:4883). SMS has
  // no such design and is simply hidden when its flag is off (Viktor,
  // 2026-09-03). A Call tab whose flag is on but which is unusable for another
  // reason (no voice-capable number, no permission) stays hidden as before.
  const emailLocked = type.isBoomCRM && !type.isCommunicationEmail;
  const voiceLocked = type.isBoomCRM && !type.isCommunicationPhone;
  const { data: eligibleApplications } = useEligibleApplications(
    chatTabAvailable ? contactId || "" : ""
  );
  const hasEligibleApplications = (eligibleApplications?.length ?? 0) > 0;
  const canonicalApplicationId = useMemo(
    () => mostRecentApplicationId(eligibleApplications),
    [eligibleApplications]
  );

  const { data: contact } = useCrmContact(contactId || "");
  // Invited but not started: a different tooltip and no Invite button.
  const invitedNotStarted = useMemo(
    () => hasOutstandingInvite(contact),
    [contact]
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const handleInviteToApply = useCallback(() => setInviteOpen(true), []);
  // Only fetched while the modal is open — the lead is not needed otherwise.
  const { data: inviteLead } = useCrmLeadDetails(
    inviteOpen && options?.inviteLeadId ? String(options.inviteLeadId) : ""
  );

  // Invite-to-apply modal, pre-filled from the CRM contact.
  const inviteModal = (
    <Show if={inviteOpen}>
      <InviteAndCreateModal
        stayOnPage
        open={inviteOpen}
        setOpen={setInviteOpen}
        leadInfo={
          contact
            ? {
                first_name: contact.first_name,
                last_name: contact.last_name,
                primary_email: contact.emails?.[0] ?? ""
              }
            : undefined
        }
        zIndex={options?.inviteModalZIndex}
        leadId={options?.inviteLeadId}
        propertyId={inviteLead?.property_id || undefined}
        propertyName={inviteLead?.property?.name}
        unitId={inviteLead?.unit_id || undefined}
        unitName={inviteLead?.unit?.title}
      />
    </Show>
  );

  const visibleTabs = useMemo(
    () =>
      composerTabs
        .filter((tab) => {
          if (tab.value === "sms") return type.isCommunicationSms;
          if (tab.value === "email")
            return type.isCommunicationEmail || emailLocked;
          if (tab.value === "voice") return isVoiceEnabled || voiceLocked;
          if (tab.value === "chat") return chatTabAvailable;
          if (tab.value === "note") return type.isBoomCRM;
          return true;
        })
        .map((tab) => {
          // The compact paywall card is the tooltip itself, so it opts out of
          // the dark tooltip bubble (which capped it at 370px and framed it).
          const lockedTab = (feature: "email" | "call") => ({
            ...tab,
            disabled: true,
            leftElement: {
              type: "icon" as const,
              component: SquareLock02Icon
            },
            disabledTooltip: (
              <BoomCrmPaywall variant="compact" {...PAYWALL_COPY[feature]} />
            ),
            disabledTooltipVariant: "card" as const
          });
          if (tab.value === "email" && emailLocked) return lockedTab("email");
          if (tab.value === "voice" && voiceLocked) return lockedTab("call");
          if (tab.value === "chat" && !hasEligibleApplications)
            return {
              ...tab,
              disabled: true,
              disabledTooltip: (
                <ChatDisabledTooltip
                  invitedNotStarted={invitedNotStarted}
                  canInvite={!options?.hideInviteToApply}
                  onInvite={handleInviteToApply}
                />
              )
            };
          return tab;
        }),
    [
      type.isCommunicationSms,
      type.isCommunicationEmail,
      emailLocked,
      voiceLocked,
      type.isBoomCRM,
      isVoiceEnabled,
      chatTabAvailable,
      hasEligibleApplications,
      invitedNotStarted,
      handleInviteToApply,
      options?.hideInviteToApply
    ]
  );

  return {
    visibleTabs,
    chatTabAvailable,
    hasEligibleApplications,
    canonicalApplicationId,
    inviteModal
  };
};
